import { useState } from 'react'
import {
    changePassword,
    requestDeleteToken,
    requestEmailChange,
    resendVerificationEmail,
    updateProfile,
} from '../api/auth'
import { apiRequest } from '../api/client'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { Field, Input, Select } from '../components/ui/Field'
import { useT } from '../i18n/context'
import { errText } from '../lib/errors'
import { useUiStore } from '../stores/ui'
import { useAuthStore } from '../stores/auth'
import { toast } from '../stores/toast'
import type { UserProfile } from '../types/api'

export function SettingsPage() {
    const t = useT()
    const user = useAuthStore(s => s.user)

    return (
        <div className='mx-auto max-w-2xl space-y-4'>
            <div>
                <h1 className='text-xl font-bold text-slate-800 dark:text-slate-100'>
                    {t('settings.title')}
                </h1>
                <p className='mt-0.5 text-sm text-slate-500 dark:text-slate-400'>
                    {t('settings.subtitle')}
                </p>
            </div>

            {user && (
                <>
                    <ProfileCard user={user} />
                    {!user.isEmailVerified && <VerifyEmailCard />}
                    <PasswordCard />
                    <EmailCard user={user} />
                    <DangerCard email={user.email} />
                </>
            )}
        </div>
    )
}

function ProfileCard({ user }: { user: UserProfile }) {
    const t = useT()
    const setUser = useAuthStore(s => s.setUser)
    const [name, setName] = useState(user.name)
    // Sprache hängt am globalen ui-Store (Navbar-Toggle und Select sind synchron)
    const language = useUiStore(s => s.language)
    const setLanguage = useUiStore(s => s.setLanguage)
    const [busy, setBusy] = useState(false)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true)
        try {
            const profile = await updateProfile({
                name: name.trim(),
                userLanguage: language,
            })
            setUser(profile)
            toast.success(t('settings.profileSaved'))
        } catch (err) {
            toast.error(errText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card>
            <CardHeader title={t('settings.profile')} />
            <CardBody>
                <form
                    onSubmit={submit}
                    className='grid grid-cols-1 gap-3 sm:grid-cols-2'
                >
                    <Field label={t('settings.name')}>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                            maxLength={128}
                        />
                    </Field>
                    <Field label={t('settings.language')}>
                        <Select
                            value={language}
                            onChange={e =>
                                setLanguage(e.target.value as 'de' | 'en')
                            }
                        >
                            <option value='de'>{t('settings.languageDe')}</option>
                            <option value='en'>{t('settings.languageEn')}</option>
                        </Select>
                    </Field>
                    <div className='sm:col-span-2 flex justify-end'>
                        <Button
                            type='submit'
                            loading={busy}
                            disabled={name === user.name && language === user.userLanguage}
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                </form>
            </CardBody>
        </Card>
    )
}

function VerifyEmailCard() {
    const t = useT()
    const [busy, setBusy] = useState(false)

    const resend = async () => {
        setBusy(true)
        try {
            await resendVerificationEmail()
            toast.success(t('settings.resent'))
        } catch (err) {
            toast.error(errText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card>
            <CardHeader title={t('settings.verifyTitle')} />
            <CardBody className='flex flex-wrap items-center justify-between gap-3'>
                <p className='text-sm text-slate-500 dark:text-slate-400'>
                    {t('settings.verifyDesc')}
                </p>
                <Button
                    variant='secondary'
                    loading={busy}
                    onClick={() => void resend()}
                >
                    {t('settings.resend')}
                </Button>
            </CardBody>
        </Card>
    )
}

function PasswordCard() {
    const t = useT()
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [repeated, setRepeated] = useState('')
    const [busy, setBusy] = useState(false)

    const mismatch =
        repeated !== '' && newPassword !== repeated
            ? t('settings.passwordMismatch')
            : null

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (newPassword !== repeated) return
        setBusy(true)
        try {
            await changePassword(currentPassword, newPassword)
            toast.success(t('settings.passwordChanged'))
            // Backend invalidiert ALLE Refresh-Tokens → abmelden.
            // RequireAuth leitet nach clear() automatisch zu /login.
            await apiRequest<void>('/auth/logout', { method: 'POST' }).catch(
                () => undefined,
            )
            useAuthStore.getState().clear()
        } catch (err) {
            toast.error(errText(err))
            setBusy(false)
        }
    }

    return (
        <Card>
            <CardHeader title={t('settings.passwordTitle')} />
            <CardBody>
                <form onSubmit={submit} className='space-y-3'>
                    <Field label={t('settings.currentPassword')}>
                        <Input
                            type='password'
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            required
                            autoComplete='current-password'
                        />
                    </Field>
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                        <Field label={t('settings.newPassword')}>
                            <Input
                                type='password'
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                minLength={8}
                                maxLength={128}
                                autoComplete='new-password'
                            />
                        </Field>
                        <Field label={t('settings.newPasswordRepeat')}>
                            <Input
                                type='password'
                                value={repeated}
                                onChange={e => setRepeated(e.target.value)}
                                required
                                minLength={8}
                                maxLength={128}
                                autoComplete='new-password'
                            />
                        </Field>
                    </div>
                    {mismatch && (
                        <p className='text-xs text-red-600 dark:text-red-400'>{mismatch}</p>
                    )}
                    <p className='text-xs text-slate-500 dark:text-slate-400'>
                        {t('settings.passwordHint')}
                    </p>
                    <div className='flex justify-end'>
                        <Button
                            type='submit'
                            loading={busy}
                            disabled={!!mismatch}
                        >
                            {t('settings.passwordTitle')}
                        </Button>
                    </div>
                </form>
            </CardBody>
        </Card>
    )
}

function EmailCard({ user }: { user: UserProfile }) {
    const t = useT()
    const [newEmail, setNewEmail] = useState('')
    const [currentPassword, setCurrentPassword] = useState('')
    const [busy, setBusy] = useState(false)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true)
        try {
            await requestEmailChange(currentPassword, newEmail.trim())
            toast.success(
                t('settings.changeSent', { email: newEmail.trim() }),
            )
            setNewEmail('')
            setCurrentPassword('')
            // pendingEmail erscheint nach dem nächsten Profil-Load
            const profile = await apiRequest<UserProfile>('/users/profile')
            useAuthStore.getState().setUser(profile)
        } catch (err) {
            toast.error(errText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card>
            <CardHeader title={t('settings.emailTitle')} />
            <CardBody className='space-y-3'>
                <p className='text-sm text-slate-500 dark:text-slate-400'>
                    {t('settings.current')} <strong>{user.email}</strong>
                </p>
                {user.pendingEmail && (
                    <div className='flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/60 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-300'>
                        <Badge tone='amber'>{t('settings.pendingBadge')}</Badge>
                        {t('settings.pendingFor', {
                            email: user.pendingEmail,
                        })}
                    </div>
                )}
                <form
                    onSubmit={submit}
                    className='grid grid-cols-1 gap-3 sm:grid-cols-2'
                >
                    <Field label={t('settings.newEmail')}>
                        <Input
                            type='email'
                            value={newEmail}
                            onChange={e => setNewEmail(e.target.value)}
                            required
                        />
                    </Field>
                    <Field label={t('settings.currentPassword')}>
                        <Input
                            type='password'
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            required
                            autoComplete='current-password'
                        />
                    </Field>
                    <div className='sm:col-span-2 flex justify-end'>
                        <Button type='submit' loading={busy}>
                            {t('settings.requestChange')}
                        </Button>
                    </div>
                </form>
            </CardBody>
        </Card>
    )
}

function DangerCard({ email }: { email: string }) {
    const t = useT()
    const [confirmEmail, setConfirmEmail] = useState('')
    const [busy, setBusy] = useState(false)
    const [requested, setRequested] = useState(false)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true)
        try {
            await requestDeleteToken(confirmEmail.trim())
            setRequested(true)
            toast.info(t('settings.deleteLinkSent'))
        } catch (err) {
            toast.error(errText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card className='border-red-200'>
            <CardHeader title={t('settings.dangerTitle')} />
            <CardBody className='space-y-3'>
                <p className='text-sm text-slate-500 dark:text-slate-400'>
                    {t('settings.dangerDesc')}
                </p>
                <form
                    onSubmit={submit}
                    className='flex flex-wrap items-end gap-2'
                >
                    <Field label={t('settings.confirmEmailLabel', { email })} className='flex-1'>
                        <Input
                            type='email'
                            value={confirmEmail}
                            onChange={e => setConfirmEmail(e.target.value)}
                            required
                        />
                    </Field>
                    <Button
                        type='submit'
                        variant='danger'
                        loading={busy}
                        disabled={
                            requested || confirmEmail.trim().toLowerCase() !== email.toLowerCase()
                        }
                    >
                        {t('settings.requestDelete')}
                    </Button>
                </form>
                {requested && (
                    <p className='rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-500 dark:text-slate-400'>
                        {t('settings.deleteRequested')}
                    </p>
                )}
            </CardBody>
        </Card>
    )
}
